import React, { useEffect, useState } from "react";
import { Gift, Plus, Play, Trash2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { fmtDate } from "../../lib/time";
import { useAuth } from "../../context/AuthContext";
import { Label, EmptyState } from "../../components/common";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Switch } from "../../components/ui/switch";

const TYPES = ["cash", "voucher", "store_credit", "physical", "badge", "recognition"];
const FREQS = ["daily", "weekly", "monthly", "quarterly", "annually"];
const NEXT = { eligible: "approved", approved: "allocated", allocated: "claimed" };
const STATUS_COLOR = { eligible: "#F59E0B", approved: "#4338CA", allocated: "#10B981", claimed: "#059669", cancelled: "#94A3B8" };

export default function Rewards() {
  const { user } = useAuth();
  const isCorp = user.role === "corporate_admin";
  const [rules, setRules] = useState([]);
  const [allocs, setAllocs] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", metric: "ssi_percent", threshold: 72, min_interactions: 10, period_days: 30, reward_type: "cash", reward_value: "R500", active: true, start_date: "", end_date: "", repeat: false, frequency: "monthly" });

  const load = () => {
    api.get("/rewards/rules").then((r) => setRules(r.data)).catch(() => {});
    api.get("/rewards/allocations").then((r) => setAllocs(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const createRule = async () => {
    try {
      await api.post("/rewards/rules", { ...form, threshold: Number(form.threshold), min_interactions: Number(form.min_interactions), period_days: Number(form.period_days) });
      toast.success("Reward rule created"); setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const evaluate = async () => {
    try { const { data } = await api.post("/rewards/evaluate"); toast.success(`${data.created} new eligible allocation(s)`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const advance = async (a, status) => {
    try { await api.patch(`/rewards/allocations/${a.id}`, { status }); toast.success(`Marked ${status}`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const delRule = async (r) => { await api.delete(`/rewards/rules/${r.id}`); toast.success("Rule removed"); load(); };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div><Label>Incentives</Label><h1 className="font-head text-2xl font-bold text-slate-900 sm:text-3xl">Rewards</h1></div>
        {isCorp && <Button onClick={evaluate} data-testid="evaluate-rewards" className="bg-[#4338CA] hover:bg-[#3730A3]"><Play className="mr-1 h-4 w-4" /> Evaluate eligibility</Button>}
      </div>

      <Tabs defaultValue="ledger">
        <TabsList>
          <TabsTrigger value="ledger" data-testid="tab-ledger">Reward Ledger</TabsTrigger>
          {isCorp && <TabsTrigger value="rules" data-testid="tab-rules">Rules</TabsTrigger>}
        </TabsList>

        <TabsContent value="ledger" className="mt-5">
          {allocs.length === 0 ? <EmptyState icon={Gift} title="No allocations" hint={isCorp ? "Create a rule and run eligibility evaluation." : "No rewards allocated yet."} /> : (
            <div className="space-y-2">
              {allocs.map((a) => (
                <div key={a.id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-slate-200 bg-white p-4" data-testid={`alloc-${a.id}`}>
                  <div>
                    <p className="font-semibold text-slate-800">{a.employee_name}</p>
                    <p className="text-xs text-slate-400">{a.rule_name} · {a.reward_type} · score {a.metric_value}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="sv-metric font-bold text-slate-800">{a.reward_value}</span>
                    <span className="rounded-full px-2.5 py-1 text-xs font-bold" style={{ color: STATUS_COLOR[a.status], background: `${STATUS_COLOR[a.status]}1a` }}>{a.status}</span>
                    {NEXT[a.status] && <Button size="sm" onClick={() => advance(a, NEXT[a.status])} data-testid={`advance-${a.id}`} className="bg-[#4338CA] hover:bg-[#3730A3]">Mark {NEXT[a.status]}</Button>}
                    {a.status !== "claimed" && a.status !== "cancelled" && <Button size="sm" variant="outline" onClick={() => advance(a, "cancelled")} data-testid={`cancel-${a.id}`} className="text-slate-500">Cancel</Button>}
                    {a.status === "claimed" && <CheckCircle2 className="h-5 w-5 text-emerald-500" />}
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {isCorp && (
          <TabsContent value="rules" className="mt-5">
            <div className="mb-4"><Button onClick={() => setOpen(true)} data-testid="add-rule" variant="outline"><Plus className="mr-1 h-4 w-4" /> New rule</Button></div>
            {rules.length === 0 ? <EmptyState icon={Gift} title="No reward rules" /> : (
              <div className="grid gap-3 sm:grid-cols-2">
                {rules.map((r) => (
                  <div key={r.id} className="rounded-xl border border-slate-200 bg-white p-5" data-testid={`rule-${r.id}`}>
                    <div className="flex items-start justify-between">
                      <p className="font-head font-semibold text-slate-900">{r.name}</p>
                      <button onClick={() => delRule(r)} data-testid={`del-rule-${r.id}`}><Trash2 className="h-4 w-4 text-slate-400 hover:text-red-500" /></button>
                    </div>
                    <p className="mt-2 text-sm text-slate-500">If Service Index ≥ <b>{r.threshold}</b> and ≥ <b>{r.min_interactions}</b> verified interactions in {r.period_days} days → <b>{r.reward_value}</b> ({r.reward_type}).</p>
                    {(r.start_date || r.next_run) && (
                      <p className="mt-2 inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-semibold text-indigo-700" data-testid={`rule-schedule-${r.id}`}>
                        {r.repeat ? `Repeats ${r.frequency}` : "One-time"}{r.next_run ? ` · next ${fmtDate(r.next_run)}` : r.start_date ? ` · from ${fmtDate(r.start_date)}` : ""}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        )}
      </Tabs>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sv-scroll">
          <DialogHeader><DialogTitle>New reward rule</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <F label="Rule name"><Input value={form.name} onChange={set("name")} data-testid="rule-name" /></F>
            <F label="Service Index threshold (0–100)"><Input type="number" value={form.threshold} onChange={set("threshold")} data-testid="rule-threshold" /></F>
            <F label="Minimum verified interactions"><Input type="number" value={form.min_interactions} onChange={set("min_interactions")} /></F>
            <F label="Period (days)"><Input type="number" value={form.period_days} onChange={set("period_days")} /></F>
            <F label="Reward type">
              <Select value={form.reward_type} onValueChange={set("reward_type")}><SelectTrigger data-testid="rule-type"><SelectValue /></SelectTrigger>
                <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t.replace("_", " ")}</SelectItem>)}</SelectContent></Select>
            </F>
            <F label="Reward value"><Input value={form.reward_value} onChange={set("reward_value")} data-testid="rule-value" /></F>
            <div className="grid grid-cols-2 gap-3">
              <F label="Start date"><Input type="date" value={form.start_date} onChange={set("start_date")} data-testid="rule-start" /></F>
              <F label="End date (optional)"><Input type="date" value={form.end_date} onChange={set("end_date")} data-testid="rule-end" /></F>
            </div>
            <div className="flex items-center justify-between rounded-lg border border-slate-200 p-3">
              <span className="text-sm font-medium text-slate-700">Repeat on a schedule</span>
              <Switch checked={form.repeat} onCheckedChange={(v) => setForm({ ...form, repeat: v })} data-testid="rule-repeat" />
            </div>
            {form.repeat && (
              <F label="Frequency">
                <Select value={form.frequency} onValueChange={set("frequency")}>
                  <SelectTrigger data-testid="rule-frequency"><SelectValue /></SelectTrigger>
                  <SelectContent>{FREQS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                </Select>
              </F>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={createRule} disabled={!form.name} data-testid="save-rule" className="bg-[#4338CA] hover:bg-[#3730A3]">Create rule</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function F({ label, children }) {
  return <div><Label className="mb-1 block normal-case tracking-normal text-slate-600">{label}</Label>{children}</div>;
}
